package meineglasfaser.frontend.admin.pages.networks

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import meineglasfaser.api.Network
import meineglasfaser.api.YaTableOptions
import meineglasfaser.api.routes.AdminRoutes
import meineglasfaser.api.routes.PrefetchRoutes
import meineglasfaser.api.routes.PrefetchedEntry
import meineglasfaser.frontend.admin.components.widgets.YaTable
import meineglasfaser.frontend.admin.components.widgets.filterOnly
import meineglasfaser.frontend.admin.components.widgets.named
import meineglasfaser.frontend.admin.components.widgets.sortedAsc
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.*
import meineglasfaser.frontend.common.components.yaform.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*

@Page("{categoryId}")
@Composable
fun NetworkCategoryPage(ctx: PageContext) {
    val networkCategories = prefetch(PrefetchRoutes.NetworkCategories())

    val categoryId = ctx.route.params["categoryId"]!!
    val category = networkCategories.firstOrNull { it.id == categoryId }

    var version by remember { mutableIntStateOf(0) }
    var showCreateModal by remember { mutableStateOf(false) }
    var showUpdateModal by remember { mutableStateOf<Network?>(null) }

    YaTable(
        header = { H1 { Text("Netze für ${category?.label ?: categoryId}") } },
        idProperty = Network::id,
        columns = listOf(
            Network::orderPos named "Position",
            Network::title named "Titel",
            Network::summary named "Kurzbeschreibung",
        ),
        initialOptions = YaTableOptions.DEFAULT.copy(
            orderBy = listOf(
                Network::orderPos.sortedAsc(),
            ),
            filters = listOf(
                Network::categoryId.filterOnly(categoryId),
            ),
        ),
        fetch = { options ->
            BackendApi.post(AdminRoutes.Networks()) {
                withAuth()
                setJsonBody(options)
            }
        },
        onNewEntry = { showCreateModal = true },
        onViewEntry = { showUpdateModal = it },
        version = version,
    )

    if (showCreateModal && category != null) {
        CreateNetworkModal(networkCategories, category.id) {
            showCreateModal = false
            version++
        }
    }
    showUpdateModal?.let { network ->
        UpdateNetworkModal(network, networkCategories) {
            showUpdateModal = null
            version++
        }
    }
}

@Composable
fun CreateNetworkModal(
    networkCategories: List<PrefetchedEntry>,
    currentCategoryId: String,
    onHide: () -> Unit,
) {
    Modal(onHide) {
        YaForm(
            initial = Network(
                id = "",
                categoryId = currentCategoryId,
                orderPos = 0,
                title = "",
                summary = "",
                details = "",
            ),
            onSubmitScope = { data ->
                val body = Network(
                    id = "",
                    categoryId = data.string(Network::categoryId),
                    orderPos = data.int(Network::orderPos),
                    title = data.string(Network::title),
                    summary = data.string(Network::summary),
                    details = data.string(Network::details),
                )
                BackendApi.post(AdminRoutes.Networks.New()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Anlegen des Netzwerks: ${error.message}"))
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Netz anlegen") }
            }
            YaFormTextInput(
                prop = Network::id,
                label = { Text("ID") },
                type = InputType.Text,
                required = false,
                disabled = true,
                placeholder = "(wird automatisch vergeben)"
            )
            YaFormDropdown(
                prop = Network::categoryId,
                label = { Text("Netzkategorie") },
                options = networkCategories,
                required = true,
                disabled = true,
            )
            YaFormNumberInput(
                prop = Network::orderPos,
                label = { Text("Position") },
                min = 0,
                required = true,
            )
            YaFormTextInput(
                prop = Network::title,
                label = { Text("Titel") },
                type = InputType.Text,
                required = true,
            )
            YaFormTextArea(
                prop = Network::summary,
                label = { Text("Kurzbeschreibung") },
                rows = 3,
                required = false,
            )
            YaFormTextArea(
                prop = Network::details,
                label = { Text("Detailbeschreibung") },
                rows = 6,
                required = false,
            )
            ModalFooterButtons(onHide, submitLabel = "Anlegen")
        }
    }
}

@Composable
fun UpdateNetworkModal(
    network: Network,
    networkCategories: List<PrefetchedEntry>,
    onHide: () -> Unit,
) {
    Modal(onHide) {
        YaForm(
            initial = network,
            onSubmitScope = { data ->
                val body = Network(
                    id = network.id,
                    categoryId = network.categoryId,
                    orderPos = data.int(Network::orderPos),
                    title = data.string(Network::title),
                    summary = data.string(Network::summary),
                    details = data.string(Network::details),
                )
                BackendApi.post(AdminRoutes.Networks.ById(network.id)) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Speichern des Netzwerks: ${error.message}"))
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Netz bearbeiten") }
            }
            YaFormTextInput(
                prop = Network::id,
                label = { Text("ID") },
                type = InputType.Text,
                required = true,
                disabled = true,
            )
            YaFormDropdown(
                prop = Network::categoryId,
                label = { Text("Netzkategorie") },
                options = networkCategories,
                required = true,
                disabled = true,
            )
            YaFormNumberInput(
                prop = Network::orderPos,
                label = { Text("Position") },
                min = 0,
                required = true,
            )
            YaFormTextInput(
                prop = Network::title,
                label = { Text("Titel") },
                type = InputType.Text,
                required = true,
            )
            YaFormTextArea(
                prop = Network::summary,
                label = { Text("Kurzbeschreibung") },
                rows = 3,
                required = false,
            )
            YaFormTextArea(
                prop = Network::details,
                label = { Text("Detailbeschreibung") },
                rows = 6,
                required = false,
            )
            ModalFooterButtons(onHide, submitLabel = "Speichern")
        }
    }
}
